import { useRef, useState } from "react";
import type { SyntheticEvent } from "react";
import { Box, Button, ClickAwayListener, IconButton, InputBase, Tooltip, Typography, CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

/** Shape returned by GET /v1/ai/sessions (ChatHistoryRepository.SessionRow). */
export interface ChatSession {
  id: number;
  userId: number;
  title: string | null;
  pinned: boolean;
  pinnedOn: string | null;
  runtimeSessionId: string;
  actorId: string;
  memoryId: string | null;
  startedOn: string;
  lastActivityOn: string;
  messageCount: number;
}

// ---------------------------------------------------------------------------
// Date filter model + helpers (also used by AIAssistant to build the API query)
// ---------------------------------------------------------------------------

export type DatePreset = "all" | "1d" | "2d" | "3d" | "7d" | "custom";

export interface DateFilter {
  preset: DatePreset;
  start: Date | null; // custom range only (local dates)
  end: Date | null;
}

export const DEFAULT_DATE_FILTER: DateFilter = { preset: "all", start: null, end: null };

const DAY_MS = 24 * 60 * 60 * 1000;

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const fmtDate = (d: Date) =>
  d.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    ...(d.getFullYear() !== new Date().getFullYear() ? { year: "numeric" as const } : {}),
  });

/**
 * Converts the filter into ISO instants for the API (?from=&to=).
 * Presets are rolling windows ("last 1 day" = the last 24 hours).
 * Custom ranges cover whole local days, start of the first day to end of the last.
 */
export function dateFilterToRange(f: DateFilter): { from: string | null; to: string | null } {
  switch (f.preset) {
    case "1d":
    case "2d":
    case "3d":
    case "7d": {
      const days = parseInt(f.preset, 10);
      return { from: new Date(Date.now() - days * DAY_MS).toISOString(), to: null };
    }
    case "custom": {
      if (!f.start) return { from: null, to: null };
      return {
        from: startOfDay(f.start).toISOString(),
        to: endOfDay(f.end ?? f.start).toISOString(),
      };
    }
    default:
      return { from: null, to: null };
  }
}

const PRESET_LABELS: Record<Exclude<DatePreset, "custom">, string> = {
  all: "All time",
  "1d": "Last 1 day",
  "2d": "Last 2 days",
  "3d": "Last 3 days",
  "7d": "Last 7 days",
};

export function dateFilterLabel(f: DateFilter): string {
  if (f.preset === "custom") {
    if (!f.start) return "Custom range";
    const end = f.end ?? f.start;
    return sameDay(f.start, end) ? fmtDate(f.start) : `${fmtDate(f.start)} – ${fmtDate(end)}`;
  }
  return PRESET_LABELS[f.preset];
}

const OPTIONS: ReadonlyArray<{ key: DatePreset; label: string }> = [
  { key: "1d", label: "Last 1 day" },
  { key: "2d", label: "Last 2 days" },
  { key: "3d", label: "Last 3 days" },
  { key: "7d", label: "Last 7 days" },
  { key: "all", label: "All time" },
  { key: "custom", label: "Custom range…" },
];

// ---------------------------------------------------------------------------
// Range calendar (no extra dependency)
// ---------------------------------------------------------------------------

interface RangeCalendarProps {
  start: Date | null;
  end: Date | null;
  onPick: (start: Date | null, end: Date | null) => void;
  isDark: boolean;
}

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

function RangeCalendar({ start, end, onPick, isDark }: RangeCalendarProps) {
  const today = startOfDay(new Date());
  const [view, setView] = useState<Date>(() => {
    const base = start ?? today;
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const lead = view.getDay(); // 0 = Sunday
  const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
  const cells: (Date | null)[] = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(view.getFullYear(), view.getMonth(), i + 1)),
  ];

  const canGoNext =
    view.getFullYear() < today.getFullYear() ||
    (view.getFullYear() === today.getFullYear() && view.getMonth() < today.getMonth());

  const accent = "#1D4ED8";
  const rangeBg = isDark ? "rgba(96,165,250,0.22)" : "rgba(59,130,246,0.16)";
  const hoverBg = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)";
  const faded = isDark ? "#4B5563" : "#CBD5E1";

  const pick = (d: Date) => {
    if (!start || end) return onPick(d, null); // start a new range
    if (d.getTime() < start.getTime()) return onPick(d, start); // earlier day becomes the start
    return onPick(start, d);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
        <IconButton
          size="small"
          aria-label="Previous month"
          onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}
          sx={{ p: 0.4, color: "inherit" }}
        >
          <ChevronLeftIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
          {view.toLocaleDateString([], { month: "long", year: "numeric" })}
        </Typography>
        <IconButton
          size="small"
          aria-label="Next month"
          disabled={!canGoNext}
          onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}
          sx={{ p: 0.4, color: "inherit", "&.Mui-disabled": { color: faded } }}
        >
          <ChevronRightIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", rowGap: "2px" }}>
        {WEEKDAYS.map((w, i) => (
          <Typography
            key={`w${i}`}
            sx={{ textAlign: "center", fontSize: "0.65rem", fontWeight: 600, color: isDark ? "#9CA3AF" : "text.secondary" }}
          >
            {w}
          </Typography>
        ))}

        {cells.map((d, i) => {
          if (!d) return <Box key={`b${i}`} />;
          const disabled = d.getTime() > today.getTime(); // sessions cannot be in the future
          const isStart = !!start && sameDay(d, start);
          const isEnd = !!end && sameDay(d, end);
          const selected = isStart || isEnd;
          const inRange =
            !!start && !!end && d.getTime() > start.getTime() && d.getTime() < end.getTime();
          const isToday = sameDay(d, today);

          return (
            <Box
              key={d.getTime()}
              role="button"
              tabIndex={disabled ? -1 : 0}
              aria-disabled={disabled}
              aria-pressed={selected}
              aria-label={d.toDateString()}
              onClick={() => !disabled && pick(d)}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && !disabled) {
                  e.preventDefault();
                  pick(d);
                }
              }}
              sx={{
                height: 28,
                lineHeight: "28px",
                textAlign: "center",
                fontSize: "0.75rem",
                cursor: disabled ? "default" : "pointer",
                borderRadius: inRange ? 0 : 1.5,
                color: disabled ? faded : selected ? "#FFFFFF" : "inherit",
                bgcolor: selected ? accent : inRange ? rangeBg : "transparent",
                border: isToday && !selected ? `1px solid ${isDark ? "#60A5FA" : accent}` : "1px solid transparent",
                boxSizing: "border-box",
                "&:hover": !disabled && !selected ? { bgcolor: hoverBg } : undefined,
                "&:focus-visible": { outline: "2px solid #60A5FA", outlineOffset: 1 },
              }}
            >
              {d.getDate()}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Filter icon + dropdown (inline, no portal: the assistant closes on outside clicks)
// ---------------------------------------------------------------------------

interface DateFilterMenuProps {
  value: DateFilter;
  onChange: (f: DateFilter) => void;
  isDark: boolean;
}

function DateFilterMenu({ value, onChange, isDark }: DateFilterMenuProps) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"presets" | "calendar">("presets");
  const [draftStart, setDraftStart] = useState<Date | null>(null);
  const [draftEnd, setDraftEnd] = useState<Date | null>(null);
  const [maxH, setMaxH] = useState<number>(420);
  const anchorRef = useRef<HTMLDivElement>(null);

  const active = value.preset !== "all";
  const muted = isDark ? "#9CA3AF" : "text.secondary";
  const hoverBg = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)";
  const border = `1px solid ${isDark ? "#303640" : "rgba(148,163,184,0.4)"}`;

  const toggle = () => {
    if (!open) {
      // Keep the dropdown inside the window: space below the button minus a margin.
      const r = anchorRef.current?.getBoundingClientRect();
      if (r) setMaxH(Math.max(220, window.innerHeight - r.bottom - 32));
      setView(value.preset === "custom" ? "calendar" : "presets");
      setDraftStart(value.start);
      setDraftEnd(value.end);
    }
    setOpen(!open);
  };

  const choose = (key: DatePreset) => {
    if (key === "custom") {
      setView("calendar");
      return;
    }
    onChange({ preset: key, start: null, end: null });
    setOpen(false);
  };

  const apply = () => {
    if (!draftStart) return;
    onChange({ preset: "custom", start: draftStart, end: draftEnd ?? draftStart });
    setOpen(false);
  };

  const draftLabel = !draftStart
    ? "Pick a start date"
    : !draftEnd
    ? `${fmtDate(draftStart)} → pick an end date`
    : sameDay(draftStart, draftEnd)
    ? fmtDate(draftStart)
    : `${fmtDate(draftStart)} – ${fmtDate(draftEnd)}`;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box
        ref={anchorRef}
        sx={{ position: "relative", flexShrink: 0 }}
        onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
      >
        <Tooltip title="Filter by date">
          <IconButton
            size="small"
            aria-label="Filter by date"
            aria-haspopup="true"
            aria-expanded={open}
            onClick={toggle}
            sx={{
              p: 0.8,
              borderRadius: 2,
              color: active ? (isDark ? "#BFDBFE" : "primary.main") : muted,
              bgcolor: active ? (isDark ? "rgba(29,78,216,0.25)" : "rgba(59,130,246,0.12)") : "transparent",
            }}
          >
            <FilterListIcon sx={{ fontSize: 19 }} />
          </IconButton>
        </Tooltip>

        {open && (
          <Box
            sx={{
              position: "absolute",
              top: "calc(100% + 4px)",
              right: 0,
              width: 244,
              maxHeight: maxH,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              zIndex: 10,
              borderRadius: 2,
              boxSizing: "border-box",
              bgcolor: isDark ? "#1E2229" : "#FFFFFF",
              color: isDark ? "#E5E7EB" : "inherit",
              border,
              boxShadow: isDark ? "0 12px 28px rgba(0,0,0,0.5)" : "0 12px 28px rgba(15,23,42,0.18)",
            }}
          >
            {view === "presets" ? (
              <Box sx={{ p: 0.75, overflowY: "auto" }}>
                {OPTIONS.map((o) => {
                  const selected = o.key === value.preset;
                  return (
                    <Box
                      key={o.key}
                      role="menuitemradio"
                      aria-checked={selected}
                      tabIndex={0}
                      onClick={() => choose(o.key)}
                      onKeyDown={(e) => e.key === "Enter" && choose(o.key)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 1,
                        py: 0.7,
                        borderRadius: 1.5,
                        cursor: "pointer",
                        fontSize: "0.82rem",
                        fontWeight: selected ? 600 : 400,
                        "&:hover": { bgcolor: hoverBg },
                        "&:focus-visible": { outline: "2px solid #60A5FA", outlineOffset: -2 },
                      }}
                    >
                      {o.label}
                      {selected && <CheckIcon sx={{ fontSize: 16, color: isDark ? "#60A5FA" : "primary.main" }} />}
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <>
                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                    <IconButton size="small" aria-label="Back to presets" onClick={() => setView("presets")} sx={{ p: 0.3, color: "inherit" }}>
                      <ChevronLeftIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 600 }}>Custom range</Typography>
                  </Box>
                  <Typography sx={{ fontSize: "0.72rem", color: muted, mb: 0.75 }}>{draftLabel}</Typography>
                  <RangeCalendar
                    start={draftStart}
                    end={draftEnd}
                    isDark={isDark}
                    onPick={(s, e) => {
                      setDraftStart(s);
                      setDraftEnd(e);
                    }}
                  />
                </Box>
                <Box sx={{ flexShrink: 0, display: "flex", justifyContent: "flex-end", gap: 0.5, p: 0.75, borderTop: border }}>
                  <Button size="small" color="inherit" onClick={() => setOpen(false)} sx={{ textTransform: "none", fontSize: "0.75rem" }}>
                    Cancel
                  </Button>
                  <Button size="small" variant="contained" disabled={!draftStart} onClick={apply} sx={{ textTransform: "none", fontSize: "0.75rem" }}>
                    Apply
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
}

// ---------------------------------------------------------------------------
// Session list
// ---------------------------------------------------------------------------

type MaybePromise = void | Promise<unknown>;
type ItemMode = "view" | "rename" | "confirmDelete";

const shortDate = (iso?: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toDateString() === new Date().toDateString()
    ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : d.toLocaleDateString([], { month: "short", day: "numeric" });
};

interface SessionItemProps {
  s: ChatSession;
  active: boolean;
  disabled: boolean;
  isDark: boolean;
  onSelect: (id: number) => void;
  onPin: (id: number, pinned: boolean) => MaybePromise;
  onRename: (id: number, title: string) => MaybePromise;
  onDelete: (id: number) => MaybePromise;
}

function SessionItem({ s, active, disabled, isDark, onSelect, onPin, onRename, onDelete }: SessionItemProps) {
  const [mode, setMode] = useState<ItemMode>("view");
  const [draft, setDraft] = useState<string>(s.title || "");

  const activeBg = isDark ? "rgba(96,165,250,0.16)" : "rgba(59,130,246,0.12)";
  const hoverBg = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)";
  const muted = isDark ? "#9CA3AF" : "text.secondary";

  const stop = (fn: () => void) => (e: SyntheticEvent) => {
    e.stopPropagation();
    fn();
  };

  const saveRename = () => {
    const title = draft.trim();
    if (!title || title === s.title) return setMode("view");
    Promise.resolve(onRename(s.id, title))
      .then(() => setMode("view"))
      .catch(() => {});
  };

  const confirmDelete = () => {
    Promise.resolve(onDelete(s.id)).catch(() => setMode("view"));
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => mode === "view" && !disabled && onSelect(s.id)}
      onKeyDown={(e) => e.key === "Enter" && mode === "view" && !disabled && onSelect(s.id)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.5,
        minHeight: 34,
        borderRadius: 1.5,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled && !active ? 0.6 : 1,
        bgcolor: active ? activeBg : "transparent",
        "&:hover": { bgcolor: active ? activeBg : hoverBg },
        "&:hover .session-date, &:focus-within .session-date": { display: mode === "view" ? "none" : undefined },
        "&:hover .session-actions, &:focus-within .session-actions": { display: "flex" },
        "&:focus-visible": { outline: "2px solid #60A5FA", outlineOffset: 1 },
        "& .MuiIconButton-root": { p: 0.4, color: muted },
      }}
    >
      {mode === "rename" ? (
        <>
          <InputBase
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") saveRename();
              if (e.key === "Escape") setMode("view");
            }}
            inputProps={{ maxLength: 200, "aria-label": "Chat title" }}
            sx={{ flex: 1, fontSize: "0.82rem", px: 0.5, borderBottom: "1px solid #60A5FA" }}
          />
          <IconButton size="small" aria-label="Save title" onClick={stop(saveRename)}>
            <CheckIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton size="small" aria-label="Cancel" onClick={stop(() => setMode("view"))}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </>
      ) : mode === "confirmDelete" ? (
        <>
          <Typography noWrap sx={{ flex: 1, fontSize: "0.82rem" }}>
            Delete this chat?
          </Typography>
          <IconButton size="small" aria-label="Confirm delete" onClick={stop(confirmDelete)}>
            <CheckIcon sx={{ fontSize: 16, color: "#DC2626" }} />
          </IconButton>
          <IconButton size="small" aria-label="Cancel" onClick={stop(() => setMode("view"))}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </>
      ) : (
        <>
          <Typography noWrap title={s.title || ""} sx={{ flex: 1, fontSize: "0.82rem", fontWeight: active ? 600 : 400 }}>
            {s.title || "New chat"}
          </Typography>
          <Typography className="session-date" variant="caption" sx={{ fontSize: "0.68rem", color: muted, flexShrink: 0 }}>
            {shortDate(s.lastActivityOn)}
          </Typography>
          <Box className="session-actions" sx={{ display: "none", flexShrink: 0 }}>
            <Tooltip title={s.pinned ? "Unpin" : "Pin"}>
              <IconButton size="small" aria-label={s.pinned ? "Unpin chat" : "Pin chat"} onClick={stop(() => onPin(s.id, !s.pinned))}>
                {s.pinned ? <PushPinIcon sx={{ fontSize: 15 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 15 }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Rename">
              <IconButton
                size="small"
                aria-label="Rename chat"
                onClick={stop(() => {
                  setDraft(s.title || "");
                  setMode("rename");
                })}
              >
                <EditOutlinedIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" aria-label="Delete chat" onClick={stop(() => setMode("confirmDelete"))}>
                <DeleteOutlineIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}
    </Box>
  );
}

export interface ChatSidebarProps {
  sessions: ChatSession[];
  loading: boolean;
  query: string;
  onQueryChange: (q: string) => void;
  dateFilter: DateFilter;
  onDateFilterChange: (f: DateFilter) => void;
  currentId: number | null;
  disabled: boolean;
  isDark: boolean;
  onNew: () => void;
  onSelect: (id: number) => void;
  onPin: (id: number, pinned: boolean) => MaybePromise;
  onRename: (id: number, title: string) => MaybePromise;
  onDelete: (id: number) => MaybePromise;
}

export default function ChatSidebar({
  sessions,
  loading,
  query,
  onQueryChange,
  dateFilter,
  onDateFilterChange,
  currentId,
  disabled,
  isDark,
  onNew,
  onSelect,
  onPin,
  onRename,
  onDelete,
}: ChatSidebarProps) {
  const muted = isDark ? "#9CA3AF" : "text.secondary";
  const filtered = dateFilter.preset !== "all";
  const pinned = sessions
    .filter((s) => s.pinned)
    .sort((a, b) => new Date(b.pinnedOn || 0).getTime() - new Date(a.pinnedOn || 0).getTime());
  const recents = sessions.filter((s) => !s.pinned); // already ordered by last activity from the API

  const renderItems = (list: ChatSession[]) =>
    list.map((s) => (
      <SessionItem
        key={s.id}
        s={s}
        active={s.id === currentId}
        disabled={disabled}
        isDark={isDark}
        onSelect={onSelect}
        onPin={onPin}
        onRename={onRename}
        onDelete={onDelete}
      />
    ));

  const sectionLabel = (text: string) => (
    <Typography
      sx={{ px: 1, pt: 1.5, pb: 0.5, fontSize: "0.68rem", fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase", color: muted }}
    >
      {text}
    </Typography>
  );

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        p: 1,
        gap: 1,
        bgcolor: isDark ? "#181A1B" : "rgba(255,255,255,0.96)",
        color: isDark ? "#E5E7EB" : "inherit",
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={() => !disabled && onNew()}
        onKeyDown={(e) => e.key === "Enter" && !disabled && onNew()}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.25,
          py: 0.9,
          borderRadius: 2,
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.6 : 1,
          fontSize: "0.85rem",
          fontWeight: 600,
          color: isDark ? "#BFDBFE" : "primary.main",
          bgcolor: isDark ? "rgba(29,78,216,0.2)" : "rgba(59,130,246,0.1)",
          "&:hover": { bgcolor: isDark ? "rgba(29,78,216,0.3)" : "rgba(59,130,246,0.16)" },
          "&:focus-visible": { outline: "2px solid #60A5FA", outlineOffset: 1 },
        }}
      >
        <AddIcon sx={{ fontSize: 18 }} /> New chat
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            px: 1,
            borderRadius: 2,
            border: `1px solid ${isDark ? "#303640" : "rgba(148,163,184,0.4)"}`,
          }}
        >
          <SearchIcon sx={{ fontSize: 17, color: muted }} />
          <InputBase
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search chats"
            inputProps={{ "aria-label": "Search chats" }}
            sx={{ flex: 1, minWidth: 0, fontSize: "0.82rem", py: 0.4, color: "inherit" }}
          />
          {query && (
            <IconButton size="small" aria-label="Clear search" onClick={() => onQueryChange("")} sx={{ p: 0.3, color: muted }}>
              <CloseIcon sx={{ fontSize: 15 }} />
            </IconButton>
          )}
        </Box>
        <DateFilterMenu value={dateFilter} onChange={onDateFilterChange} isDark={isDark} />
      </Box>

      {filtered && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            alignSelf: "flex-start",
            pl: 1,
            pr: 0.4,
            py: 0.2,
            borderRadius: 4,
            fontSize: "0.72rem",
            fontWeight: 600,
            color: isDark ? "#BFDBFE" : "primary.main",
            bgcolor: isDark ? "rgba(29,78,216,0.25)" : "rgba(59,130,246,0.12)",
          }}
        >
          {dateFilterLabel(dateFilter)}
          <IconButton
            size="small"
            aria-label="Clear date filter"
            onClick={() => onDateFilterChange(DEFAULT_DATE_FILTER)}
            sx={{ p: 0.2, color: "inherit" }}
          >
            <CloseIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", scrollbarWidth: "thin" }}>
        {loading && sessions.length === 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={18} />
          </Box>
        )}

        {pinned.length > 0 && (
          <>
            {sectionLabel("Pinned")}
            {renderItems(pinned)}
          </>
        )}

        {recents.length > 0 && (
          <>
            {sectionLabel("Recents")}
            {renderItems(recents)}
          </>
        )}

        {!loading && sessions.length === 0 && (
          <Typography sx={{ px: 1, py: 2, fontSize: "0.8rem", color: muted }}>
            {query || filtered ? "No chats match your filters." : "No chats yet. Ask a question to get started."}
          </Typography>
        )}
      </Box>
    </Box>
  );
}